import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Radio, Copy, ExternalLink, RefreshCcw, Check, X, Play, Loader2, Video, Save } from "lucide-react";

function money(cents) {
  return `$${(Number(cents || 0) / 100).toFixed(2)}`;
}

function StatusBadge({ d }) {
  if (d.rejected) return <span className="px-2 py-0.5 border border-[var(--ff-border)] text-[var(--ff-text-dim)] font-mono-ui text-[0.55rem] uppercase tracking-[0.15em]">Rejected</span>;
  if (d.approved) return <span className="px-2 py-0.5 border border-green-500/50 text-green-300 font-mono-ui text-[0.55rem] uppercase tracking-[0.15em]">Approved</span>;
  return <span className="px-2 py-0.5 border border-yellow-500/50 text-yellow-300 font-mono-ui text-[0.55rem] uppercase tracking-[0.15em]">Pending</span>;
}

function VideoBadge({ status }) {
  const map = {
    pending: { t: "Video generating…", c: "text-[var(--ff-red)]" },
    ready: { t: "Video ready", c: "text-green-300" },
    failed: { t: "Video failed", c: "text-yellow-300" },
    none: { t: "Text-only", c: "text-[var(--ff-text-dim)]" },
  };
  const m = map[status] || map.none;
  return (
    <span className={`inline-flex items-center gap-1 font-mono-ui text-[0.55rem] uppercase tracking-[0.15em] ${m.c}`}>
      {status === "pending" ? <Loader2 size={10} className="animate-spin" /> : <Video size={10} />} {m.t}
    </span>
  );
}

export default function HostDonations() {
  const [ov, setOv] = useState(null);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const origin = window.location.origin;

  const loadOverlay = useCallback(async () => {
    try {
      const { data } = await api.get("/host/overlay");
      setOv(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load overlay settings");
    }
  }, []);

  const loadDonations = useCallback(async () => {
    try {
      const { data } = await api.get("/host/donations");
      setDonations(data || []);
    } catch { /* silent on poll */ }
  }, []);

  useEffect(() => {
    (async () => { await Promise.all([loadOverlay(), loadDonations()]); setLoading(false); })();
    const t = setInterval(loadDonations, 8000);
    return () => clearInterval(t);
  }, [loadOverlay, loadDonations]);

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copied`);
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put("/host/overlay/settings", {
        video_min_amount: ov.video_min_amount,
        auto_approve: ov.auto_approve,
        alert_duration_secs: ov.alert_duration_secs,
        show_logo_url: ov.show_logo_url,
        durations_allowed: ov.durations_allowed,
      });
      setOv(data);
      toast.success("Overlay settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSavingSettings(false); }
  };

  const regenerate = async () => {
    if (!window.confirm("Generate a NEW overlay URL? Your old OBS link will stop working and you'll need to paste the new one.")) return;
    try {
      const { data } = await api.post("/host/overlay/regenerate");
      setOv(data);
      toast.success("New overlay URL generated — update OBS");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const act = async (id, action) => {
    setBusyId(id + action);
    try {
      await api.post(`/host/donations/${id}/${action}`);
      toast.success(action === "approve" ? "Approved — will air when ready" : action === "reject" ? "Rejected" : "Replayed to overlay");
      await loadDonations();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusyId(null); }
  };

  const toggleDuration = (d) => {
    const cur = ov.durations_allowed || [];
    const next = cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort((a, b) => a - b);
    setOv({ ...ov, durations_allowed: next.length ? next : [4] });
  };

  if (loading) return <div className="p-8 text-[var(--ff-text-dim)] font-mono-ui text-sm">Loading donation alerts…</div>;
  if (!ov) return (
    <div className="surface-card p-10 text-center" data-testid="host-donations-not-linked">
      <div className="font-display text-2xl mb-2">NO HOST CARD LINKED.</div>
      <p className="text-[var(--ff-text-dim)] text-sm">An admin needs to link your Discord account to a host record first.</p>
    </div>
  );

  const overlayUrl = `${origin}${ov.overlay_path}`;
  const donateUrl = `${origin}${ov.donation_path}`;
  const pending = donations.filter((d) => !d.approved && !d.rejected);
  const history = donations.filter((d) => d.approved || d.rejected);

  return (
    <div className="space-y-8" data-testid="host-donations">
      {/* Links */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="surface-card p-6" data-testid="host-overlay-url-card">
          <div className="label-eyebrow mb-3 flex items-center gap-2 text-[var(--ff-red)]"><Radio size={12} /> ▮ OBS OVERLAY URL</div>
          <p className="text-xs text-[var(--ff-text-dim)] font-mono-ui mb-3 leading-relaxed">
            Add this as a <strong>Browser Source</strong> in OBS (1280×720, transparent). Keep it secret — anyone with the link can see your alerts.
          </p>
          <div className="flex gap-2 mb-3">
            <input readOnly value={overlayUrl} className="flex-1 px-3 py-2 text-xs font-mono-ui" data-testid="host-overlay-url" />
            <button onClick={() => copy(overlayUrl, "Overlay URL")} className="btn-ghost text-xs whitespace-nowrap" data-testid="host-overlay-copy"><Copy size={12} /> Copy</button>
          </div>
          <div className="flex gap-2">
            <a href={ov.overlay_path} target="_blank" rel="noreferrer" className="btn-ghost text-xs" data-testid="host-overlay-open"><ExternalLink size={12} /> Preview Overlay</a>
            <button onClick={regenerate} className="btn-ghost text-xs" data-testid="host-overlay-regen"><RefreshCcw size={12} /> New URL</button>
          </div>
        </div>

        <div className="surface-card p-6" data-testid="host-donate-url-card">
          <div className="label-eyebrow mb-3 flex items-center gap-2">▮ YOUR DONATION PAGE</div>
          <p className="text-xs text-[var(--ff-text-dim)] font-mono-ui mb-3 leading-relaxed">
            Share this public link so fans can donate and trigger alerts on your stream.
          </p>
          <div className="flex gap-2 mb-3">
            <input readOnly value={donateUrl} className="flex-1 px-3 py-2 text-xs font-mono-ui" data-testid="host-donate-url" />
            <button onClick={() => copy(donateUrl, "Donation link")} className="btn-ghost text-xs whitespace-nowrap" data-testid="host-donate-copy"><Copy size={12} /> Copy</button>
          </div>
          <a href={ov.donation_path} target="_blank" rel="noreferrer" className="btn-ghost text-xs" data-testid="host-donate-open"><ExternalLink size={12} /> Open Donation Page</a>
        </div>
      </div>

      {/* Settings */}
      <div className="surface-card p-6" data-testid="host-overlay-settings">
        <div className="label-eyebrow mb-4">▮ ALERT SETTINGS</div>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="text-[0.65rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)] block mb-1">Video unlock threshold ($)</label>
            <input type="number" min="0" step="1" value={ov.video_min_amount} onChange={(e) => setOv({ ...ov, video_min_amount: Number(e.target.value) })} className="w-full px-3 py-2 text-sm" data-testid="host-setting-video-min" />
          </div>
          <div>
            <label className="text-[0.65rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)] block mb-1">Text alert duration (secs)</label>
            <input type="number" min="3" max="30" value={ov.alert_duration_secs} onChange={(e) => setOv({ ...ov, alert_duration_secs: Number(e.target.value) })} className="w-full px-3 py-2 text-sm" data-testid="host-setting-duration" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[0.65rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)] block mb-1">Show logo URL (overlay top-left)</label>
            <input type="url" value={ov.show_logo_url || ""} onChange={(e) => setOv({ ...ov, show_logo_url: e.target.value })} placeholder="Leave blank to auto-use your show logo / avatar" className="w-full px-3 py-2 text-sm font-mono-ui" data-testid="host-setting-logo" />
          </div>
          <div>
            <label className="text-[0.65rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)] block mb-2">Allowed video lengths</label>
            <div className="flex gap-2">
              {[4, 8].map((d) => (
                <button key={d} type="button" onClick={() => toggleDuration(d)} className={`px-4 py-2 text-sm border ${(ov.durations_allowed || []).includes(d) ? "bg-[var(--ff-red)] text-white border-[var(--ff-red)]" : "border-[var(--ff-border)] hover:border-white"}`} data-testid={`host-setting-dur-${d}`}>{d}s</button>
              ))}
            </div>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-3 cursor-pointer" data-testid="host-setting-autoapprove-label">
              <input type="checkbox" checked={!!ov.auto_approve} onChange={(e) => setOv({ ...ov, auto_approve: e.target.checked })} className="w-4 h-4 accent-[var(--ff-red)]" data-testid="host-setting-autoapprove" />
              <span className="text-sm">Auto-approve alerts <span className="text-[var(--ff-text-dim)] font-mono-ui text-xs">(skip moderation)</span></span>
            </label>
          </div>
        </div>
        <div className="pt-5 mt-5 border-t border-[var(--ff-border)] flex justify-end">
          <button onClick={saveSettings} disabled={savingSettings} className="btn-primary" data-testid="host-settings-save"><Save size={14} /> {savingSettings ? "Saving…" : "Save Settings"}</button>
        </div>
      </div>

      {/* Moderation queue */}
      <div>
        <div className="label-eyebrow mb-4 text-[var(--ff-red)]" data-testid="host-pending-header">▮ PENDING APPROVAL · {pending.length}</div>
        {pending.length === 0 ? (
          <div className="surface-card p-8 text-center text-[var(--ff-text-dim)] text-sm font-mono-ui" data-testid="host-pending-empty">
            No alerts waiting. New donations will appear here for you to approve before they air.
          </div>
        ) : (
          <div className="space-y-4">
            {pending.map((d) => (
              <DonationRow key={d.id} d={d} busyId={busyId} onAct={act} showApprove />
            ))}
          </div>
        )}
      </div>

      {history.length > 0 && (
        <div>
          <div className="label-eyebrow mb-4 text-[var(--ff-text-dim)]">▮ HISTORY · {history.length}</div>
          <div className="space-y-4">
            {history.map((d) => (
              <DonationRow key={d.id} d={d} busyId={busyId} onAct={act} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DonationRow({ d, busyId, onAct, showApprove }) {
  const videoUrl = d.video_status === "ready" ? `${process.env.REACT_APP_BACKEND_URL}/api/donation/video/${d.id}` : null;
  return (
    <div className="surface-card p-5" data-testid={`host-donation-${d.id}`}>
      <div className="flex flex-col md:flex-row gap-5">
        {videoUrl && (
          <video src={videoUrl} controls className="w-full md:w-64 aspect-video bg-black rounded" data-testid={`host-donation-video-${d.id}`} />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            {d.donor_avatar && <img src={d.donor_avatar} alt="" className="w-8 h-8 rounded-full object-cover ring-1 ring-[var(--ff-border)]" />}
            <span className="font-display text-xl">{d.donor_name || "Anonymous"}</span>
            <span className="font-display text-lg text-[var(--ff-red)]">{money(d.amount_cents)}</span>
            <div className="ml-auto flex items-center gap-2">
              <StatusBadge d={d} />
            </div>
          </div>
          {d.message && <div className="text-sm text-[var(--ff-text)] mb-2 break-words">{d.message}</div>}
          <div className="flex items-center gap-3 flex-wrap mb-3">
            <VideoBadge status={d.video_status} />
            {d.character && <span className="font-mono-ui text-[0.55rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">· {d.character}</span>}
            {d.video_prompt && <span className="font-mono-ui text-[0.55rem] text-[var(--ff-text-dim)] truncate">"{d.video_prompt}"</span>}
          </div>
          <div className="flex gap-2">
            {showApprove && (
              <>
                <button onClick={() => onAct(d.id, "approve")} disabled={busyId === d.id + "approve"} className="btn-primary text-xs" data-testid={`host-approve-${d.id}`}>
                  <Check size={12} /> Approve
                </button>
                <button onClick={() => onAct(d.id, "reject")} disabled={busyId === d.id + "reject"} className="btn-ghost text-xs" data-testid={`host-reject-${d.id}`}>
                  <X size={12} /> Reject
                </button>
              </>
            )}
            {d.approved && (
              <button onClick={() => onAct(d.id, "replay")} disabled={busyId === d.id + "replay"} className="btn-ghost text-xs" data-testid={`host-replay-${d.id}`}>
                <Play size={12} /> Replay to Overlay
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
